import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Divider } from "@mui/material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import BasicProfileDetail from "./BasicProfileDetail";
import CustomTabs from "components/Shared/CustomTabs";
import CustomTabPanel from "components/Shared/CustomTabPanel";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";

import { useProfileUserQuery } from "store/services/userApi";

import { useAuth, useAppMeta } from "hooks";
import {
  DeleteOutlineOutlined,
  EditOutlined,
  LockOutlined,
} from "@mui/icons-material";
import { BaseButtonGroup } from "components/Shared/Base/BaseButtonGroup";

const UserProfileContext = React.createContext(null);

export const useUserProfileContext = () => React.useContext(UserProfileContext);

const UserProfileScreen = () => {
  const { loggedInUser, isAdmin, isCustomer } = useAuth();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Profile";

  const breadcrumbs = [
    {
      title: "My Profile",
    },
  ];

  const userProfileTabs = [{ label: "My Detail", value: "Profile" }];

  // RTK Query
  const {
    data: userProfile,
    isLoading: loadingUserProfile,
    error,
  } = useProfileUserQuery(loggedInUser._id);

  useAppMeta(`My ${activeTab}`);

  return (
    <UserProfileContext.Provider value={{ loadingUserProfile, userProfile }}>
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={loadingUserProfile}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />

        <BreadcrumbLayout.Paper>
          {(isAdmin || isCustomer) && (
            <CustomTabs tabs={userProfileTabs} activeTab={activeTab} />
          )}

          {!loadingUserProfile ? (
            <>
              <CustomTabPanel activeTab={activeTab} value="Profile">
                <BasicProfileDetail />
              </CustomTabPanel>
            </>
          ) : (
            <>
              <InfoGridSkeleton />

              <Divider />

              <RowsSkeleton />
            </>
          )}
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </UserProfileContext.Provider>
  );
};

export default UserProfileScreen;

const PageActions = () => {
  const { isAdmin, isContractor, isVendorStaff, isVendor, isDriver } =
    useAuth();

  const navigate = useNavigate();

  const ACTIONS = [
    {
      label: "Edit",
      icon: <EditOutlined fontSize="small" />,
      disabled: isAdmin,
    },
    {
      label: "Change Password",
      icon: <LockOutlined fontSize="small" />,
      variant: "outlined",
    },
    ...(isContractor || isVendorStaff || isVendor || isDriver
      ? [
          {
            label: "Delete My Account",
            icon: <DeleteOutlineOutlined fontSize="small" />,
            color: "error",
          },
        ]
      : []),
  ];

  // methods
  const handleGroupedActions = async (actionIndex) => {
    switch (actionIndex) {
      case 0:
        navigate("/app/me/update");
        break;

      case 1:
        navigate("/app/me/change-password");
        break;

      case 2:
        navigate("/app/delete");
        break;

      default:
        break;
    }
  };
  return (
    <BaseButtonGroup
      actions={ACTIONS}
      handleSelectedAction={handleGroupedActions}
    />
  );
};
